import React, { useEffect, useState } from 'react';
import { PhoneCall } from 'lucide-react';

const STEPS = [
  { key: 'initiating', label: 'Initiating callback...',                   sub: 'Preparing your secure session' },
  { key: 'ringing',    label: 'Calling mobile ending in 4421...',          sub: 'Ringing — please answer' },
  { key: 'connecting', label: 'Authenticated session continuing by voice', sub: 'Connecting you now...' },
];

// Play a dual-tone phone ring (480 Hz + 440 Hz) — classic POTS ring
function playRing(audioCtx, startAt) {
  const osc1 = audioCtx.createOscillator();
  const osc2 = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  osc1.frequency.value = 480;
  osc2.frequency.value = 440;
  osc1.type = 'sine';
  osc2.type = 'sine';

  const t = startAt;
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.07, t + 0.06);
  gain.gain.setValueAtTime(0.07, t + 1.4);
  gain.gain.linearRampToValueAtTime(0, t + 1.55);

  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(audioCtx.destination);

  osc1.start(t);
  osc2.start(t);
  osc1.stop(t + 1.6);
  osc2.stop(t + 1.6);
}

const CallbackTransition = ({ onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    // Web Audio phone rings
    let audioCtx = null;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const resume = audioCtx.state === 'suspended' ? audioCtx.resume() : Promise.resolve();
      resume.then(() => {
        const now = audioCtx.currentTime;
        playRing(audioCtx, now + 0.8);  // ring when "ringing" step shows
        playRing(audioCtx, now + 2.5);  // second ring
      }).catch(() => {});
    } catch (_) {}

    const t1 = setTimeout(() => setStepIdx(1), 800);
    const t2 = setTimeout(() => setStepIdx(2), 2000);
    const t3 = setTimeout(() => { setShow(false); onComplete(); }, 2800);

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      audioCtx?.close().catch(() => {});
    };
  }, [onComplete]);

  const step = STEPS[stepIdx];
  const isRinging = stepIdx === 1;

  return (
    <div className="h-full flex flex-col items-center justify-center bg-gradient-to-b from-[#1a3a6b] to-[#0a1430] rounded-2xl relative overflow-hidden">

      {/* Custom keyframes */}
      <style>{`
        @keyframes ripple-out {
          0%   { transform: scale(0.95); opacity: 0.45; }
          100% { transform: scale(2.6);  opacity: 0; }
        }
        @keyframes phone-ring {
          0%,  100% { transform: rotate(0deg); }
          15% { transform: rotate(-18deg); }
          30% { transform: rotate(18deg); }
          45% { transform: rotate(-12deg); }
          60% { transform: rotate(12deg); }
          75% { transform: rotate(-6deg); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.15); }
          50%       { box-shadow: 0 0 0 24px rgba(255,255,255,0); }
        }
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Background radial glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)' }}
        />
      </div>

      {/* Ripple rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {[0, 0.6, 1.2, 1.8].map((delay, i) => (
          <div
            key={i}
            className="absolute rounded-full border border-white/20"
            style={{
              width: 100,
              height: 100,
              animation: `ripple-out ${isRinging ? '2s' : '3.5s'} ease-out ${delay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Phone icon */}
      <div
        className="relative z-10 w-24 h-24 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 mb-10"
        style={{
          animation: isRinging
            ? 'glow-pulse 1s ease-in-out infinite, phone-ring 0.7s ease-in-out infinite'
            : 'glow-pulse 3s ease-in-out infinite',
        }}
      >
        <PhoneCall
          className="w-10 h-10 transition-colors duration-500"
          style={{ color: stepIdx === 2 ? '#86efac' : 'white' }}
        />
      </div>

      {/* Step label — fades between steps */}
      <div
        key={stepIdx}
        className="relative z-10 text-center px-10"
        style={{ animation: 'fade-up 0.4s ease-out both' }}
      >
        <p className="text-white text-xl font-semibold mb-2">{step.label}</p>
        <p className="text-white/50 text-sm">{step.sub}</p>
      </div>

      {/* Ringing sound-wave bars */}
      {isRinging && (
        <div
          className="relative z-10 flex items-end gap-1 mt-8 h-6"
          style={{ animation: 'fade-up 0.3s ease-out both' }}
        >
          {[4, 8, 14, 20, 14, 8, 4].map((h, i) => (
            <div
              key={i}
              className="w-1 rounded-full bg-white/50"
              style={{
                height: h,
                animation: `glow-pulse ${0.5 + i * 0.07}s ease-in-out ${i * 0.08}s infinite alternate`,
                opacity: 0.6 + Math.abs(i - 3) * -0.1,
              }}
            />
          ))}
        </div>
      )}

      {/* Progress bar */}
      <div className="absolute bottom-8 left-10 right-10 z-10">
        <div className="h-px bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-white/40 rounded-full"
            style={{
              width: `${(stepIdx / (STEPS.length - 1)) * 100}%`,
              transition: 'width 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </div>
        <p className="text-white/25 text-xs text-center mt-2">Secure session transfer</p>
      </div>
    </div>
  );
};

export default CallbackTransition;
